import type { Wall } from '../domain.js';

type CellKey = string; // `${col},${row}`

/**
 * Grid-based spatial index для быстрого wall lookup.
 * Стена маппится на все grid-ячейки, которые пересекает её AABB.
 * Обратный индекс wall→cells для O(1) remove.
 *
 * Supports adaptive cell size based on wall density.
 */
export class SpatialIndex {
  private cellSize: number;
  private readonly defaultCellSize: number;
  private readonly autoSize: boolean;
  private readonly grid = new Map<CellKey, Set<string>>();
  private readonly wallCells = new Map<string, CellKey[]>();
  private readonly wallMap = new Map<string, Wall>();

  constructor(cellSize: number = 64, autoSize: boolean = true) {
    this.cellSize = cellSize;
    this.defaultCellSize = cellSize;
    this.autoSize = autoSize;
  }

  /**
   * Compute optimal cell size based on wall count.
   * Larger cells for denser maps reduces bucket iteration overhead.
   */
  static optimalCellSize(wallCount: number): number {
    if (wallCount <= 100) return 64;
    if (wallCount <= 500) return 128;
    return 256;
  }

  /** Построить индекс из массива стен (полный rebuild) */
  build(walls: Wall[]): void {
    this.grid.clear();
    this.wallCells.clear();
    this.wallMap.clear();

    // Auto-adjust cell size based on wall density
    if (this.autoSize) {
      this.cellSize = SpatialIndex.optimalCellSize(walls.length);
    }

    for (const wall of walls) {
      this.insert(wall);
    }
  }

  /** Добавить одну стену в индекс */
  insert(wall: Wall): void {
    const cs = this.cellSize;
    const minX = Math.min(wall.x1, wall.x2);
    const maxX = Math.max(wall.x1, wall.x2);
    const minY = Math.min(wall.y1, wall.y2);
    const maxY = Math.max(wall.y1, wall.y2);

    const colMin = Math.floor(minX / cs);
    const colMax = Math.floor(maxX / cs);
    const rowMin = Math.floor(minY / cs);
    const rowMax = Math.floor(maxY / cs);

    const cells: CellKey[] = [];

    for (let col = colMin; col <= colMax; col++) {
      for (let row = rowMin; row <= rowMax; row++) {
        const key: CellKey = `${col},${row}`;
        let bucket = this.grid.get(key);
        if (!bucket) {
          bucket = new Set();
          this.grid.set(key, bucket);
        }
        bucket.add(wall.id);
        cells.push(key);
      }
    }

    this.wallCells.set(wall.id, cells);
    this.wallMap.set(wall.id, wall);
  }

  /** Удалить стену по ID */
  remove(wallId: string): void {
    const cells = this.wallCells.get(wallId);
    if (!cells) return;

    for (const key of cells) {
      const bucket = this.grid.get(key);
      if (bucket) {
        bucket.delete(wallId);
        if (bucket.size === 0) {
          this.grid.delete(key);
        }
      }
    }

    this.wallCells.delete(wallId);
    this.wallMap.delete(wallId);
  }

  /** Получить стены, пересекающие AABB (x±range, y±range) */
  query(x: number, y: number, range: number): Wall[] {
    const cs = this.cellSize;
    const minX = x - range;
    const maxX = x + range;
    const minY = y - range;
    const maxY = y + range;

    const colMin = Math.floor(minX / cs);
    const colMax = Math.floor(maxX / cs);
    const rowMin = Math.floor(minY / cs);
    const rowMax = Math.floor(maxY / cs);

    const seen = new Set<string>();
    const result: Wall[] = [];

    for (let col = colMin; col <= colMax; col++) {
      for (let row = rowMin; row <= rowMax; row++) {
        const key: CellKey = `${col},${row}`;
        const bucket = this.grid.get(key);
        if (!bucket) continue;
        for (const wallId of bucket) {
          if (!seen.has(wallId)) {
            seen.add(wallId);
            const wall = this.wallMap.get(wallId);
            if (wall) result.push(wall);
          }
        }
      }
    }

    return result;
  }

  /**
   * Viewport-aware query: intersects range AABB with viewport AABB.
   * Returns same or fewer walls than `query` (never more).
   *
   * @param x - center x of query
   * @param y - center y of query
   * @param range - query range (AABB half-size)
   * @param vpMinX - viewport min X (world coords)
   * @param vpMinY - viewport min Y (world coords)
   * @param vpMaxX - viewport max X (world coords)
   * @param vpMaxY - viewport max Y (world coords)
   */
  queryViewport(
    x: number, y: number, range: number,
    vpMinX: number, vpMinY: number,
    vpMaxX: number, vpMaxY: number,
  ): Wall[] {
    const cs = this.cellSize;

    // Intersect range AABB with viewport AABB
    const minX = Math.max(x - range, vpMinX);
    const maxX = Math.min(x + range, vpMaxX);
    const minY = Math.max(y - range, vpMinY);
    const maxY = Math.min(y + range, vpMaxY);

    // If intersection is empty, return empty
    if (minX > maxX || minY > maxY) return [];

    const colMin = Math.floor(minX / cs);
    const colMax = Math.floor(maxX / cs);
    const rowMin = Math.floor(minY / cs);
    const rowMax = Math.floor(maxY / cs);

    const seen = new Set<string>();
    const result: Wall[] = [];

    for (let col = colMin; col <= colMax; col++) {
      for (let row = rowMin; row <= rowMax; row++) {
        const key: CellKey = `${col},${row}`;
        const bucket = this.grid.get(key);
        if (!bucket) continue;
        for (const wallId of bucket) {
          if (!seen.has(wallId)) {
            seen.add(wallId);
            const wall = this.wallMap.get(wallId);
            if (wall) result.push(wall);
          }
        }
      }
    }

    return result;
  }

  /** Число стен в индексе */
  get size(): number {
    return this.wallMap.size;
  }

  /** Current cell size (may differ from constructor after build with autoSize) */
  get currentCellSize(): number {
    return this.cellSize;
  }
}
